import React from 'react';
import { motion } from 'motion/react';
import { cn } from '../lib/utils';

interface Prism3DProps {
  color: string;
  stars: number;
  isCorrupted: boolean;
  isMystery?: boolean;
  className?: string;
  isStatic?: boolean;
  potentialColors?: string[];
  potentials?: { type: string }[];
}

const potentialColorMap: Record<string, string> = {
  gc_multi: "#fbbf24",
  gc_bonus: "#fbbf24", // Legacy
  exp_bonus: "#10b981",
  reward_multi: "#f97316",
  hybrid_bonus: "#f97316", // Legacy
  reward_flat: "#6366f1",
  score_bonus: "#a855f7",
  fever_bonus: "#f43f5e",
  time_bonus: "#38bdf8"
};

const Prism3D = ({ color, stars, isCorrupted, isMystery, className, isStatic = false, potentialColors = [], potentials }: Prism3DProps) => {
  const colors = {
    red: "#f43f5e", 
    yellow: "#fbbf24", 
    green: "#10b981", 
    blue: "#38bdf8", 
    violet: "#a855f7"
  };
  
  const baseColor = isCorrupted ? "#4338ca" : (colors[color as keyof typeof colors] || "#94a3b8");
  
  // New Hierarchy Logic
  // 1 Star: 1 ring
  // 2 Star: 2 rings
  // 3 Star: 3 rings
  // 4 Star: 0 rings, bigger sphere, emitting light
  
  const ringCount = stars <= 3 ? stars : 0;
  const isUltimate = stars === 4;

  const resolvedPotentialColors = potentials 
    ? potentials.map(p => p && p.type ? potentialColorMap[p.type] || "#ffffff" : "#ffffff")
    : potentialColors;

  return (
    <motion.div 
      className={cn("relative flex items-center justify-center w-full h-full", className)}
      animate={isMystery ? { 
        filter: ["hue-rotate(0deg)", "hue-rotate(360deg)"] 
      } : {}}
      transition={{ 
        duration: isMystery ? 3 : 0, 
        repeat: Infinity, 
        ease: "linear" 
      }}
    >
      {/* Background Glow - Stronger for 4 Star */}
      <motion.div 
        className={cn("absolute inset-0 rounded-full", isUltimate ? "blur-3xl opacity-60" : "blur-2xl opacity-20")}
        style={{ backgroundColor: baseColor }}
        animate={isStatic ? {
          opacity: isUltimate ? 0.4 : 0.1,
          scale: isUltimate ? 1 : 0.9
        } : (isUltimate ? { 
          opacity: [0.4, 0.6, 0.4],
          scale: [1, 1.2, 1]
        } : {
          opacity: [0.1, 0.2, 0.1],
          scale: [0.9, 1.1, 0.9]
        })}
        transition={isStatic ? { duration: 0 } : { duration: 4, repeat: Infinity, ease: "easeInOut" }}
      />
      
      <svg viewBox="0 0 100 100" className="w-full h-full relative z-10 overflow-visible">
        <defs>
          <radialGradient id={`grad-${color}-${stars}`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={isUltimate ? "#ffffff" : baseColor} stopOpacity={1} />
            <stop offset={isUltimate ? "40%" : "70%"} stopColor={baseColor} stopOpacity={0.9} />
            <stop offset="100%" stopColor={baseColor} stopOpacity={0} />
          </radialGradient>
          
          {isCorrupted && (
            <filter id="corrupted-noise">
              <feTurbulence type="fractalNoise" baseFrequency="0.5" numOctaves="3" result="noise" />
              <feDisplacementMap in="SourceGraphic" in2="noise" scale="5" />
            </filter>
          )}
        </defs>
 
        {/* The Core */}
        <motion.g
          style={{ 
            filter: isCorrupted ? "url(#corrupted-noise)" : "none"
          }}
          animate={isStatic ? {
            scale: 1,
            filter: isUltimate ? "drop-shadow(0 0 10px rgba(255,255,255,0.4))" : "none"
          } : (isUltimate ? {
            scale: [1, 1.15, 1],
            filter: ["drop-shadow(0 0 10px white)", "drop-shadow(0 0 25px white)", "drop-shadow(0 0 10px white)"]
          } : {
            scale: [0.98, 1.02, 0.98]
          })}
          transition={isStatic ? { duration: 0 } : { duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          {isUltimate ? (
            /* Large Glowing Sphere for Ultimate Tier */
            <circle cx="50" cy="50" r="35" fill={`url(#grad-${color}-${stars})`} />
          ) : (
            /* Standard Core for 1-3 Stars */
            <circle cx="50" cy="50" r={stars === 1 ? 16 : stars === 2 ? 21 : 26} fill={`url(#grad-${color}-${stars})`} />
          )}
        </motion.g>

        {/* Bright Center Pulse for 4 Star */}
        {isUltimate && (
          <motion.circle 
            cx="50" cy="50" r="8" 
            fill="white" 
            animate={isStatic ? { scale: 1, opacity: 0.7 } : { scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
            transition={isStatic ? { duration: 0 } : { duration: 1.5, repeat: Infinity }}
            className="blur-[2px]" 
          />
        )}

        {/* Orbit 1 (Horizontal for 1-Star, angled for 2 & 3 Stars) */}
        {ringCount >= 1 && (() => {
          const rotation = stars === 1 ? 0 : 15;
          const path = "M 8,50 A 42,14 0 1,0 92,50 A 42,14 0 1,0 8,50 Z";
          const ballColor = resolvedPotentialColors[0] || "#ffffff";
          return (
            <g transform={`rotate(${rotation} 50 50)`}>
              {/* Path hidden as per user request */}
              {isStatic ? (
                <circle cx="8" cy="50" r="5" fill={ballColor} />
              ) : (
                <circle r="5" fill={ballColor}>
                  <animateMotion dur="4s" repeatCount="indefinite" path={path} />
                </circle>
              )}
            </g>
          );
        })()}

        {/* Orbit 2 (Angled) */}
        {ringCount >= 2 && (() => {
          const rotation = 75;
          // Reverse direction for Orbit 2 (sweep-flag is 1 instead of 0)
          const path = "M 12,50 A 38,18 0 1,1 88,50 A 38,18 0 1,1 12,50 Z";
          const ballColor = resolvedPotentialColors[1] || "#ffffff";
          return (
            <g transform={`rotate(${rotation} 50 50)`}>
              {/* Path hidden */}
              {isStatic ? (
                <circle cx="12" cy="50" r="4.5" fill={ballColor} />
              ) : (
                <circle r="4.5" fill={ballColor}>
                  <animateMotion dur="6s" repeatCount="indefinite" path={path} />
                </circle>
              )}
            </g>
          );
        })()}

        {/* Orbit 3 (Angled) */}
        {ringCount >= 3 && (() => {
          const rotation = 135;
          const path = "M 5,50 A 45,12 0 1,0 95,50 A 45,12 0 1,0 5,50 Z";
          const ballColor = resolvedPotentialColors[2] || "#ffffff";
          return (
            <g transform={`rotate(${rotation} 50 50)`}>
              {/* Path hidden */}
              {isStatic ? (
                <circle cx="5" cy="50" r="4" fill={ballColor} />
              ) : (
                <circle r="4" fill={ballColor}>
                  <animateMotion dur="8s" repeatCount="indefinite" path={path} />
                </circle>
              )}
            </g>
          );
        })()}
      </svg>
      
      {/* Backdrop Blur Overlay */}
      <div className={cn("absolute inset-0 rounded-full pointer-events-none", !isUltimate && "backdrop-blur-[1px]")} />
    </motion.div>
  );
};

export default Prism3D;
